import { useState, useEffect, useCallback } from 'react';
import {
  getContracts,
  getContractById,
  createContract,
  updateContract,
  deleteContract,
} from '../services/apvApi';

export const APV_QUERY_KEY = ['apv-contracts'];

export function useApvContracts(projectId) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  const activeProjectId = projectId || (() => {
    try {
      const p = localStorage.getItem('projectDetails');
      if (!p) return null;
      const parsed = JSON.parse(p);
      return parsed?.projectNumber || parsed?.projectId || parsed?.id || parsed?.proj_number || null;
    } catch { return null; }
  })();

  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    setError(null);
    try {
      const contracts = await getContracts(activeProjectId);
      setData(contracts);
      return contracts;
    } catch (err) {
      console.warn('API fetch contracts fallback or error:', err);
      setIsError(true);
      setError(err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [activeProjectId]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch: fetchContracts,
    setData,
  };
}

// Hook: Fetch single APV contract
export function useApvContract(id) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    setIsLoading(true);
    getContractById(id)
      .then((contract) => {
        if (isMounted) setData(contract);
      })
      .catch((err) => {
        console.error('Error fetching contract by id:', err);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [id]);

  return { data, isLoading };
}

// Hook: Create APV contract
export function useCreateApvContract() {
  const mutateAsync = useCallback(async (newContract) => {
    return await createContract(newContract);
  }, []);

  return { mutateAsync };
}

// Hook: Update APV contract
export function useUpdateApvContract() {
  const mutateAsync = useCallback(async ({ id, ...contractData }) => {
    return await updateContract(id, contractData);
  }, []);

  return { mutateAsync };
}

// Hook: Delete APV contract
export function useDeleteApvContract() {
  const mutateAsync = useCallback(async (id) => {
    return await deleteContract(id);
  }, []);

  return { mutateAsync };
}
